// Counsel-review surface (C3.2) — super-tenant reviewers triage AI drafts flagged by the
// self-check (C3.1). Lists the pending queue + records a verdict via the admin-worker.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ReviewRow = Record<string, unknown>;

export function CounselReviewRoute() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["counsel-reviews"],
    queryFn: () => api.listCounselReviews(),
  });
  const rows = (data ?? []) as ReviewRow[];

  const verdict = useMutation({
    mutationFn: (v: { id: string; verdict: "approved" | "changes_requested" | "rejected" }) => api.submitCounselVerdict(v.id, v.verdict),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["counsel-reviews"] }),
  });

  const columns: Column<ReviewRow>[] = [
    {
      key: "aiSelfCheckScore", header: "Self-check",
      render: (r) => (
        <span className={cn("font-medium", Number(r.aiSelfCheckScore) < 0.7 ? "text-destructive" : "text-foreground")}>
          {r.aiSelfCheckScore != null ? Number(r.aiSelfCheckScore).toFixed(3) : "—"}
        </span>
      ),
    },
    { key: "contentType", header: "Content type", render: (r) => humanize(r.contentType) },
    { key: "prefillRunId", header: "Prefill run", render: (r) => `${String(r.prefillRunId ?? "—").slice(0, 8)}…` },
    { key: "createdAt", header: "Created", render: (r) => (r.createdAt ? String(r.createdAt).slice(0, 16).replace("T", " ") : "—") },
  ];

  return (
    <section>
      <PageHeader
        title="Counsel review"
        description="AI drafts flagged below the self-check threshold, awaiting sign-off (lowest score first)."
        actions={isFetching ? <span className="text-sm text-muted-foreground">updating…</span> : undefined}
      />
      {error && <p className="mb-4 text-sm text-destructive">Failed: {(error as Error).message}</p>}
      {!isLoading && !error && rows.length === 0 && <p className="text-sm text-emerald-700">Queue is clear — nothing pending. ✓</p>}
      {verdict.isError && (
        <p className="mb-4 text-sm text-destructive">
          {verdict.error instanceof ApiError ? `Server ${verdict.error.status}: ${JSON.stringify(verdict.error.body)}` : (verdict.error as Error).message}
        </p>
      )}

      {(isLoading || rows.length > 0) && (
        <DataTable
          columns={columns}
          rows={rows}
          loading={isLoading}
          getRowId={(r) => String(r.id)}
          onRowClick={(r) => navigate({ to: "/counsel/$reviewId", params: { reviewId: String(r.id) } })}
          empty="Queue is clear — nothing pending."
          rowActions={(r) => {
            const id = r.id as string;
            return (
              <div className="flex justify-end gap-1.5">
                <Button size="xs" variant="outline" disabled={verdict.isPending} onClick={() => verdict.mutate({ id, verdict: "approved" })}>Approve</Button>
                <Button size="xs" variant="outline" disabled={verdict.isPending} onClick={() => verdict.mutate({ id, verdict: "changes_requested" })}>Changes</Button>
                <Button size="xs" variant="destructive" disabled={verdict.isPending} onClick={() => verdict.mutate({ id, verdict: "rejected" })}>Reject</Button>
              </div>
            );
          }}
        />
      )}
    </section>
  );
}
